import type { AuthProviderProps } from "react-oidc-context";
import { OidcClient, UserManager, WebStorageStateStore } from "oidc-client-ts";

// Single shared UserManager. Constructing one instance and passing it to
// AuthProvider via the `userManager` prop (rather than letting AuthProvider
// build one from the spread config) means the same instance survives any
// StrictMode/HMR re-renders of the provider tree — its in-memory `_pending`
// promise dedupes overlapping signinCallback() calls in the same tick.
//
// Storage choices:
// - userStore = localStorage: tokens survive tab close (right behaviour for a
//   partner portal). XSS risk mitigated by CSP; HttpOnly cookies aren't an
//   option for a pure SPA without a BFF.
// - stateStore = sessionStorage: per-tab signin state. Avoids the multi-tab
//   localStorage race documented in
//   https://github.com/authts/react-oidc-context/issues/1501 (gundy's comment),
//   and matches what the official sample-keycloak-react-oidc-context uses.
// CHP-419: `offline_access` makes Authentik issue a refresh token, which
// oidc-client-ts uses to silently renew the 30-min access token via the
// refresh-token grant (automaticSilentRenew, no hidden iframe). Without it the
// session hard-expires at 30 min mid-task. The Authentik provider must also
// GRANT the scope or it is silently dropped — keep this in lockstep with the
// offline_access scope mapping in infra/compose/authentik/bootstrap.yaml.
export const OIDC_SCOPE = "openid email profile groups offline_access";

const oidcSettings = {
  authority: import.meta.env.VITE_OIDC_AUTHORITY,
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
  redirect_uri: import.meta.env.VITE_OIDC_REDIRECT_URI,
  post_logout_redirect_uri: import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI,
  response_type: "code",
  scope: OIDC_SCOPE,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  automaticSilentRenew: true,
};

export const userManager = new UserManager(oidcSettings);

// `OidcClient.createSigninRequest()` is the public entry point for
// building an OIDC authorize URL + storing PKCE/state/nonce in the
// stateStore — without navigating. UserManager wraps this internally
// for `signinRedirect()` but doesn't expose it as a public method
// (ADR-0010 needs the URL to pass to the backend's flow-executor
// proxy). We instantiate a parallel OidcClient sharing the same
// settings/stateStore so a later signinCallback() can find the state
// the same way it would for a normal redirect flow.
export const oidcClient = new OidcClient(oidcSettings);

export const oidcConfig: AuthProviderProps = {
  userManager,
  // We process the OIDC callback explicitly inside AuthCallback.tsx. The
  // AuthProvider's built-in handler runs in a useEffect that fires twice
  // under React 19 StrictMode (and again on Vite HMR mid-flow). The first
  // call to userManager.signinCallback() succeeds and removes the state from
  // sessionStorage; the second call throws "No matching state found in
  // storage" and surfaces as a Sign-in failed page. See
  // https://github.com/authts/react-oidc-context/issues/1501
  // https://github.com/authts/react-oidc-context/issues/1587
  // https://github.com/authts/oidc-client-ts/issues/1487 (atomovski's comment
  // documents this exact `skipSigninCallback` workaround).
  //
  // skipSigninCallback is a documented AuthProviderProps option — when true,
  // hasAuthParams() in the URL is ignored and no auto-callback is performed.
  // Source: AuthProvider.tsx line ~280 in react-oidc-context main:
  //   if (hasAuthParams() && !skipSigninCallback) {
  //       user = await userManager.signinCallback();
  //   }
  skipSigninCallback: true,
  // onSigninCallback fires only when AuthProvider processes the callback.
  // Since we skip that path, this is effectively unused — the URL-cleanup
  // happens inside AuthCallback after our explicit signinCallback() call.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};
